import React, { useRef } from 'react';
import { Box } from '@mui/material';
import CartLoader from '../../../../Helper/Loaders/CartLoader';
import { useTranslation } from 'react-i18next';
import { calculateDynamicRange, getTooltipHtml, swapIndexForMultiYAxis } from '../../../../Helper/DigitalTwin/DigitalTwinFunctions';
import BaseVitalChart from '../../components/BaseVitalChart';
import {
    getBaseCanvasSettings,
    getBaseChartSettings,
    getBaseTooltipSettings,
    getBaseXAxisSettings,
    isBaseDataSeriesEmpty
} from '../../../../Helper/DigitalTwin/VitalChartConfigurations';


const VitalsChartArea = ({
    chartID = 'vitals-chart',
    series, metrics, categories,
    startDate,
    isLoading,
    primaryGranularity,
    resetToLatestDataDate = () => { },
    visibleSeries,
    onZoom = () => {},
    onZoomReset = () => {},
    zoomedRange = null,
    setLoading = null,
    isZoomed = false
}) => {

    const zoomResetViewRef = useRef(null);
    const { t } = useTranslation();

    const getChartSettings = () => {
        return getBaseChartSettings(chartID, onZoom, setLoading)
    }

    const getYAxisSettings = () => {
        const yAxisSettings = [];
        const swapedMetrics = swapIndexForMultiYAxis(metrics)

        for (let i in swapedMetrics) {
            let metric = swapedMetrics[i]
            let avgSeriesItem = series.find(s => s.key === metric.key && s.type !== 'rangeArea')
            let rangeSeriesItem = series.find(s => s.key === metric.key && s.type === 'rangeArea')
            let seriesItem = avgSeriesItem || rangeSeriesItem

            if (!seriesItem) {
                continue;
            }

            let ranges = metric && metric.key ? metric.ranges : seriesItem.ranges

            // Combine avg data and flattened min/max range data so the y-axis
            // is scaled to include min/max values, not just the average line.
            let combinedData = [...(avgSeriesItem?.data || [])]
            if (rangeSeriesItem?.data) {
                rangeSeriesItem.data.forEach(point => {
                    if (point?.y && Array.isArray(point.y)) {
                        combinedData.push({ y: point.y[0] }, { y: point.y[1] })
                    }
                })
            }
            ranges = calculateDynamicRange(combinedData, ranges.min, ranges.max)

            let rightAxis = i > 1;
            let offsetX = -35;

            if (i == 0 || i == 2) {
                offsetX = -60;
            }

            offsetX = -15;

            yAxisSettings.push({
                seriesName: [seriesItem.name, `${seriesItem.name} mix`],
                opposite: rightAxis,
                showAlways: true,
                tickAmount: 5,
                position: 'top',
                min: ranges.min,
                max: ranges.max,
                axisTicks: {
                    show: false,
                    borderType: 'dotted'
                },
                axisBorder: {
                    show: true,
                    color: metric.color,
                    offsetY: 0,
                    offsetX: -7
                },
                title: {
                    text: seriesItem.unit, // Y Axis Label
                    position: 'high',
                    rotate: 0,
                    offsetX: rightAxis ? -30 : 30,
                    offsetY: -160,
                    style: {
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#686868',
                    },
                },
                labels: {
                    show: true,
                    offsetX: offsetX,
                    offsetY: 0,
                    style: {
                        colors: '#87939D',
                        fontSize: '10px',
                        fontWeight: 500,
                        fontfamily: 'Roboto !important'
                    },
                    formatter: (value) => Math.round(value),
                    // formatter: (value) => {
                    //     let updatedValue = parseFloat(value)
                    //     return updatedValue ? updatedValue?.toFixed(metric.yAxisDecimalPlaces || 0) : updatedValue;
                    // },
                },
            })
        }

        return yAxisSettings;
    }

    const getXAxisSettings = () => {
        const settings = getBaseXAxisSettings(primaryGranularity, categories, startDate, zoomedRange)
        delete settings.min
        delete settings.max
        return { ...settings }
    }

    const getToolTipSettings = () => {
        const tooltipSettings = getBaseTooltipSettings()
        tooltipSettings.custom = (opts) => getTooltipHtml(opts, metrics, t, visibleSeries)
        return tooltipSettings
    }

    const isDataSeriesEmpty = () => {
        return isBaseDataSeriesEmpty(series)
    }

    const getCanvasSettings = () => {
        const baseCanvasSettings = getBaseCanvasSettings(primaryGranularity, series)
        const fillOpacities = series.map(s => s.type === 'rangeArea' ? 0.15 : 1);
        const strokeWidths = series.map(s => s.type === 'rangeArea' ? 0 : 2);
        const metricColorMap = metrics.reduce((acc, metric, index) => {
            acc[metric.key] = metric.color;
            return acc;
        }, {});

        const dynamicColors = series.map(s => metricColorMap[s.key] || '#000000');

        baseCanvasSettings.colors = dynamicColors

        baseCanvasSettings.fill = {
            type: 'solid',
            opacity: fillOpacities
        }
        baseCanvasSettings.stroke = {
            width: strokeWidths,
            curve: 'straight',
        }

        return baseCanvasSettings
    }

    const getChartOptions = () => {
        return {
            ...getCanvasSettings(),
            chart: getChartSettings(),
            xaxis: getXAxisSettings(),
            yaxis: getYAxisSettings(),
            tooltip: getToolTipSettings(),
        };
    }

    if (isLoading) {
        return (
            <Box sx={{ minHeight: '360px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CartLoader />
            </Box>
        );
    }

    return (
        <BaseVitalChart
            options={getChartOptions()}
            series={series}
            chartID={chartID}
            chartHeight={360}
            primaryGranularity={primaryGranularity}
            resetToLatestDataDate={resetToLatestDataDate}
            isDataSeriesEmpty={isDataSeriesEmpty}
            zoomResetViewRef={zoomResetViewRef}
            onZoomReset={onZoomReset}
            isZoomed={isZoomed}
        />
    );
};

export default VitalsChartArea;
